import React, { useMemo } from 'react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { ChartDataPoint, ColorScheme, COLOR_PALETTES } from './chartUtils';

interface PieChartProps {
  data: ChartDataPoint[];
  colorScheme: ColorScheme;
  title?: string;
}

const MAX_PIE_SLICES = 6;

export const PieChart: React.FC<PieChartProps> = ({ data, colorScheme, title }) => {
  // Aggregate items beyond MAX_PIE_SLICES into "Others"
  const chartData = useMemo(() => {
    if (data.length === 0) return [];

    if (data.length <= MAX_PIE_SLICES) {
      return data;
    }

    const top5 = data.slice(0, 5);
    const others = data.slice(5);
    const othersSum = others.reduce((sum, item) => sum + item.value, 0);

    return [...top5, { label: 'Others', value: othersSum }];
  }, [data]);

  // Calculate total for center display
  const total = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.value, 0);
  }, [chartData]);

  // Empty state
  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-neutral-500 dark:text-neutral-400">
        <p className="text-sm">No data available</p>
      </div>
    );
  }

  // Get colors based on color scheme
  const getColor = (index: number): string => {
    return COLOR_PALETTES.categorical[index % COLOR_PALETTES.categorical.length];
  };

  return (
    <div>
      {title && (
        <h4 className="text-sm font-semibold text-neutral-900 dark:text-white mb-3">
          {title}
        </h4>
      )}
      <ResponsiveContainer width="100%" height={320}>
        <RechartsPieChart margin={{ top: 10, right: 10, bottom: 60, left: 10 }}>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="40%"
            innerRadius={52.5}
            outerRadius={75}
            paddingAngle={2}
            labelLine={false}
            stroke="none"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getColor(index)}
              />
            ))}
          </Pie>

          <Tooltip
            contentStyle={{
              backgroundColor: '#2a2a2f',
              border: '1px solid #3f3f46',
              borderRadius: '6px',
              fontSize: '12px'
            }}
            labelStyle={{ color: '#f3f4f6' }}
            itemStyle={{ color: '#d1d5db' }}
            formatter={(value: number) => [value.toLocaleString(), 'Count']}
          />

          <Legend
            layout="horizontal"
            align="center"
            verticalAlign="bottom"
            iconType="circle"
            iconSize={8}
            wrapperStyle={{
              fontSize: '12px',
              paddingTop: '10px'
            }}
            formatter={(value: string) => (
              <span style={{ color: '#d1d5db', fontSize: '12px' }}>{value}</span>
            )}
          />

          {/* Center text — explicit SVG attributes required, Tailwind won't apply */}
          <text
            x="50%"
            y="40%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#f5f5f5"
            fontSize={18}
            fontWeight="bold"
          >
            {total.toLocaleString()}
          </text>
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
};
